import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import type { InboxPage } from './inbox';
import { isInboxListQuery, isInboxTabListQuery } from './keys';

import type { GetInquiriesV2Response } from '@/services/getInquiriesV2';

const INBOX_DEBOUNCE_MS = 750;

const pendingInboxInvalidation = new WeakMap<QueryClient, ReturnType<typeof setTimeout>>();

/**
 * Refetches the inbox list once for a burst of reasons to. The Primum Support
 * account receives a hint for every member's reply, so without this a busy
 * hour would refetch the list on every event. The first request in a window
 * schedules the refetch and the rest join it.
 */
export function invalidateInboxDebounced(
  queryClient: QueryClient,
  delayMs: number = INBOX_DEBOUNCE_MS
): void {
  if (pendingInboxInvalidation.has(queryClient)) {
    return;
  }

  const timeoutId = setTimeout(() => {
    pendingInboxInvalidation.delete(queryClient);

    queryClient
      .invalidateQueries({ predicate: isInboxListQuery, refetchType: 'active' })
      .catch(() => undefined);
  }, delayMs);

  pendingInboxInvalidation.set(queryClient, timeoutId);
}

/**
 * Clears a support thread's unread flag in every loaded inbox tab, so its row
 * stops reading as unread the moment the thread is marked read rather than at
 * the next list refetch.
 */
export function markInboxSupportThreadRead(queryClient: QueryClient, conversationId: string): void {
  queryClient.setQueriesData<InfiniteData<InboxPage>>({ predicate: isInboxListQuery }, (data) =>
    data
      ? {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            supportThreads: page.supportThreads.map((entry) =>
              entry.conversationId === conversationId && entry.unread
                ? { ...entry, unread: false }
                : entry
            ),
          })),
        }
      : data
  );
}

/**
 * Marks an inquiry's feedback submitted in the viewer's open and closed tabs, flat and grouped by
 * question alike, so the row stops asking for feedback without waiting for a refetch. A grouped page
 * holds the same inquiries, so the same patch lands in it.
 */
export function markInboxFeedbackSubmitted(
  queryClient: QueryClient,
  userId: string | undefined,
  conversationId: string
): void {
  (['open', 'closed'] as const).forEach((tab) => {
    queryClient.setQueriesData<InfiniteData<GetInquiriesV2Response>>(
      { predicate: (query) => isInboxTabListQuery(query, tab, userId) },
      (prev) => {
        if (!prev?.pages) {
          return prev;
        }

        return {
          ...prev,
          pages: prev.pages.map((page) => ({
            ...page,
            inquiries: page.inquiries?.map((inquiry) =>
              inquiry.conversationId === conversationId
                ? { ...inquiry, feedbackStatus: 'Submitted' as const }
                : inquiry
            ),
          })),
        };
      }
    );
  });
}
